/**
 * 数据字典Hook - 统一管理数据字典的使用
 * 优化版本：优先使用本地缓存，提升性能，防重复调用
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { localDictCacheManager } from '../utils/localDictCacheManager';

export interface DictItem {
  id: number;
  dictLabel: string;
  dictValue: string;
  dictSort: number;
  status: number;
  cssClass?: string;
  listClass?: string;
  isDefault?: string;
  remark?: string;
}

export interface DictOptions {
  dictType: string;
  labelKey?: string; // 显示字段，默认 'dictLabel'
  valueKey?: string; // 值字段，默认 'dictValue'
  sortKey?: string; // 排序字段，默认 'dictSort'
  filter?: (item: DictItem) => boolean; // 过滤函数
  transform?: (item: DictItem) => any; // 转换函数
}

// 全局字典数据缓存，避免重复加载
const globalDictCache = new Map<string, DictItem[]>();
const globalLoadingPromises = new Map<string, Promise<DictItem[]>>();

/**
 * 清理全局字典缓存
 */
export const clearGlobalDictCache = () => {
  globalDictCache.clear();
  globalLoadingPromises.clear();
};

/**
 * 数据字典Hook
 * @param dictType 字典类型
 * @param options 配置选项
 * @returns 字典数据和方法
 */
export const useDict = (dictType: string, options: DictOptions = { dictType }) => {
  const [dictData, setDictData] = useState<DictItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  const {
    labelKey = 'dictLabel',
    valueKey = 'dictValue',
    sortKey = 'dictSort',
    filter,
    transform
  } = options;

  // 加载字典数据
  const loadDictData = useCallback(async () => {
    if (!dictType) return;
    
    // 字典数据接口是公开的，不需要token也能访问（网站端使用）
    // 检查用户是否已登录的逻辑已移除，允许无token访问

    // 检查全局缓存
    if (globalDictCache.has(dictType)) {
      const cachedData = globalDictCache.get(dictType)!;
      if (isMountedRef.current) {
        setDictData(cachedData);
      }
      return;
    }

    // 检查是否正在加载中
    const existingPromise = globalLoadingPromises.get(dictType);
    if (existingPromise) {
      try {
        const data = await existingPromise;
        if (isMountedRef.current) {
          setDictData(data);
        }
      } catch (err) {
        if (isMountedRef.current) {
          setError(err instanceof Error ? err.message : '加载字典数据失败');
        }
      }
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // 确保缓存管理器已初始化
      await localDictCacheManager.initialize();
      
      // 从本地缓存获取数据（优先本地缓存，其次服务器）
      const promise = localDictCacheManager.getDictData(dictType);
      globalLoadingPromises.set(dictType, promise);
      
      const data = await promise;
      
      // 更新全局缓存
      globalDictCache.set(dictType, data);
      
      // 应用过滤条件
      let processedData = data;
      if (filter) {
        processedData = processedData.filter(filter);
      }
      
      // 应用转换函数
      if (transform) {
        processedData = processedData.map(transform);
      }
      
      // 按排序字段排序
      processedData.sort((a: any, b: any) => (a[sortKey] || 0) - (b[sortKey] || 0));
      
      if (isMountedRef.current) {
        setDictData(processedData);
      }
    } catch (err) {
      console.error(`加载字典数据失败 (${dictType}):`, err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : '加载字典数据失败，请稍后重试');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
      globalLoadingPromises.delete(dictType);
    }
  }, [dictType, filter, transform, sortKey]);

  // 获取字典选项（用于Select组件）
  const getOptions = useCallback(() => {
    return dictData.map(item => ({
      label: (item as any)[labelKey],
      value: (item as any)[valueKey],
      ...item
    }));
  }, [dictData, labelKey, valueKey]);

  // 根据值获取标签
  const getLabelByValue = useCallback((value: string | number) => {
    const item = dictData.find(item => (item as any)[valueKey] === value);
    return item ? (item as any)[labelKey] : value;
  }, [dictData, labelKey, valueKey]);

  // 根据标签获取值
  const getValueByLabel = useCallback((label: string) => {
    const item = dictData.find(item => (item as any)[labelKey] === label);
    return item ? (item as any)[valueKey] : label;
  }, [dictData, labelKey, valueKey]);

  // 获取默认值
  const getDefaultValue = useCallback(() => {
    const defaultItem = dictData.find(item => item.isDefault === 'Y');
    return defaultItem ? (defaultItem as any)[valueKey] : undefined;
  }, [dictData, valueKey]);

  // 刷新数据
  const refresh = useCallback(() => {
    loadDictData();
  }, [loadDictData]);

  useEffect(() => {
    loadDictData();
    
    // 清理函数
    return () => {
      isMountedRef.current = false;
    };
  }, [loadDictData]);

  return {
    dictData,
    options: getOptions(),
    loading,
    error,
    getLabelByValue,
    getValueByLabel,
    getDefaultValue,
    refresh
  };
};

/**
 * 批量获取多个字典类型的数据
 * @param dictTypes 字典类型数组
 * @returns 字典数据映射
 */
export const useDicts = (dictTypes: string[]) => {
  const [dictsData, setDictsData] = useState<Record<string, DictItem[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDictsData = useCallback(async () => {
    if (!dictTypes.length) return;
    
    // 字典数据接口是公开的，不需要token也能访问（网站端使用）
    // 检查用户是否已登录的逻辑已移除，允许无token访问
    
    setLoading(true);
    setError(null);
    
    try {
      // 确保缓存管理器已初始化
      await localDictCacheManager.initialize();
      
      const promises = dictTypes.map(async (dictType) => {
        const data = await localDictCacheManager.getDictData(dictType);
        return { dictType, data };
      });

      const results = await Promise.all(promises);
      const dictsMap: Record<string, DictItem[]> = {};
      
      results.forEach(({ dictType, data }) => {
        dictsMap[dictType] = data;
      });
      
      setDictsData(dictsMap);
    } catch (err) {
      console.error('批量加载字典数据失败:', err);
      setError('批量加载字典数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [dictTypes]);

  const refresh = useCallback(() => {
    loadDictsData();
  }, [loadDictsData]);

  useEffect(() => {
    loadDictsData();
  }, [loadDictsData]);

  return {
    dictsData,
    loading,
    error,
    refresh
  };
};

export default useDict;